import { Data } from "effect"
import type { Sweepstake } from "../../core/sweepstakes/sweepstake.ts"

export namespace Profile {
  export type User = {
    id: string
    memberSince: string
    username: string
    attendedShowIds: Array<string>
    ownedMerchSkuIds: Array<string>
  }

  export interface Show {
    id: string
    name: string
    month: string
    dayOfMonth: string
    dayOfWeek: string
    date: Date
    time: string
    venue: string
    city: string
    state: string
    url: string
  }

  export type CheckInPromptInfo = {
    show: Show
  }

  export type Merch = {
    id: string
    name: string
    title: string
    description: string
    imageSrc: string
    buyLink?: string
    attributes: Array<Attribute>
  }

  export interface BaseTrack {
    id: string
    title: string
    artist: string
  }

  export interface ReleasedTrack extends BaseTrack {
    duration: number
    playbackLink: {
      url: string
      expiresAt: string
    }
  }

  export interface UnreleasedTrack extends BaseTrack {
    releaseDate: string
  }

  export type Track = Data.TaggedEnum<{
    Released: ReleasedTrack
    Unreleased: UnreleasedTrack
  }>

  export const Track = Data.taggedEnum<Track>()

  export interface BasePlaylist {
    id: string
    artist: string
    coverImageUrl: string
    title: string
  }

  export interface ReleasedPlaylist extends BasePlaylist {
    tracks: Array<Track>
    isDownloadable: boolean
  }

  export interface PartiallyReleasedPlaylist extends BasePlaylist {
    releaseDate: string
    tracks: Array<Track>
  }

  export interface UnreleasedPlaylist extends BasePlaylist {
    releaseDate: string
    tracks: Array<Track>
  }

  export type Playlist = Data.TaggedEnum<{
    Released: ReleasedPlaylist
    PartiallyReleased: PartiallyReleasedPlaylist
    Unreleased: UnreleasedPlaylist
  }>

  export const Playlist = Data.taggedEnum<Playlist>()

  export type Exclusive = Data.TaggedEnum<{
    Gallery: {
      id: string
      title: string
      coverImage: string
      media: Array<{
        id: string
        title: string
        src: string
        description: string
      }>
    }
    Video: {
      id: string
      title: string
      subtitle?: string
      coverImage: string
      videoSrc: string
    }
    Sweepstake: {
      id: string
      title: string
      subtitle?: string
      coverImage: string
      isWinner: boolean
      announcementDate: string
      description: string
      status: Sweepstake.Status
    }
    Playlist: { id: string; playlist: Playlist }
  }>

  export const Exclusive = Data.taggedEnum<Exclusive>()

  export type Collection = {
    username: string
    collectables: Array<Collectable>
    merchCount: number
    showCount: number
  }

  export type Collectable = Data.TaggedEnum<{
    Item: {
      id: string
      title: string
      description: string
      attributes: Array<Attribute>
      date: number
      imageSrc: string
      color: `#${string}`
      url?: string
    }
    ShowCheckIn: {
      id: string
      title: string
      description: string
      attributes: Array<Attribute>
      date: number
      imageSrc: string
      color: `#${string}`
    }
  }>

  export const Collectable = Data.taggedEnum<Collectable>()

  export type Attribute = {
    type: string
    value: string
  }
}

export type LoginStep = Data.TaggedEnum<{
  Login: {}
  VerifyOtp: { email: string }
}>

export const LoginStep = Data.taggedEnum<LoginStep>()
